// Operator summary for an agent-queue job: the SHORT, structured view the
// human reviewer reads instead of the verbose spec / build / approval
// markdown the agents pass to each other. The markdown stays untouched; this
// sits beside it.
//
// `ai_jobs.operator_summary` (jsonb, migration 20260909150000) holds
// { spec?, build?, approval? }. cockpit-cli validates and writes one stage per
// submit; this module only reads it. The design gallery uses a hand-written
// fixture for one real job (fixtures/petesLoom.ts).

import type { AiJob } from './supabase.types';
import { supabase } from './supabase';
import { aiJobKind } from './aiJobLabels';
import { PETES_LOOM_CLIENT_NAME, PETES_LOOM_JOB_ID } from '../components/ai/operator/fixtures/petesLoom';

export type JobSourceKind = 'trello' | 'loom' | 'email' | 'comment' | 'note' | 'chat' | 'other';

/** Where a request came from: the Trello card, a Loom, an email thread... */
export type JobSource = {
  id: string;
  kind: JobSourceKind;
  label: string;
  url?: string;
};

/** Icon glyph (ui/Icon) for each source kind. */
export const JOB_SOURCE_ICON: Record<JobSourceKind, string> = {
  trello: 'clipboard-check',
  loom: 'video',
  email: 'mail',
  comment: 'messages-square',
  note: 'pencil',
  chat: 'message-square',
  other: 'link',
};

export type JobRequestKind = 'build' | 'question' | 'bug' | 'answer';

export type JobRisk = 'email' | 'payments' | 'auth' | 'prod-data' | 'sms';

/** One thing the requester asked for, in plain language, in their order. */
export type JobRequest = {
  /** 'R1', 'R2', ... */
  id: string;
  /** JobSource id */
  source: string;
  text: string;
  kind?: JobRequestKind;
  risks?: JobRisk[];
};

/** What the spec proposes to do about one request. */
export type ProposedAction = {
  /** JobRequest id */
  request: string;
  text: string;
};

/** A call the operator needs to make before or at approval. */
export type Decision = {
  id: string;
  question: string;
  options?: string[];
  recommendation?: string;
  /** One plain sentence: why the recommended option wins. Shown under the options. */
  why?: string;
  answer?: string | null;
};

/** One heading of a content job's article outline, in page order. */
export type OutlineHeading = {
  level: 1 | 2 | 3 | 4;
  text: string;
  /** One line on what the section covers. */
  intent?: string;
};

/** The human's adjusted outline. The build agent follows it instead of `outline`. */
export type OutlineOverride = {
  headings: OutlineHeading[];
  note?: string | null;
  updated_at: string;
};

/**
 * Page Build jobs (task_type 'page_build'): one section of the page, in page order. `copy` is the section's draft
 * text as markdown; `purpose` says what the section has to do for the reader (and the search); `proof` lists the
 * evidence it carries (a project, a review, a number), so a section never ships as template filler.
 */
/**
 * One section of a Page Build, as direction rather than finished copy: what the section is and the idea it has to
 * get across. The section's builder writes the copy and designs it. `source` holds material the page is expected to
 * carry as given (client copy, a quote, a price table), only when there is some; `image` marks the few sections
 * that should use a generated image.
 */
export type PageSection = {
  id: string;
  /** Working heading; the builder may sharpen it. */
  heading: string;
  /** 1 = the page's H1, 2 or 3 = a section heading. */
  level?: 1 | 2 | 3;
  /** What the section is: its job on the page. */
  purpose: string;
  /** The general idea it has to get across. Direction, not copy. */
  message: string;
  /** Material to include as given, with where it came from. Only when the job has source material. */
  source?: string | null;
  /** Set on the sections that should use a generated image: what the image shows and why it's there. */
  image?: { idea: string } | null;
  /** The page's one or two signature moments: the bold animation or interaction. Every other section stays restrained. */
  signature?: boolean;
};

/**
 * Page Build jobs: the person the page is for, written before any section. Sections are ordered to persuade this
 * buyer into `action`, in their words; the buyer test after the build checks the page against it.
 */
export type PageBuyer = {
  /** Who searches this and what situation they are in. */
  who: string;
  /** The outcome they want, in their words. */
  wants: string;
  /** What they already know or believe (about the problem, the market, us: usually nothing). */
  knows: string;
  /** What holds them back from acting. */
  objections: string[];
  /** The one promise the page makes them. */
  promise: string;
  /** The one action the page asks for. */
  action: string;
};

/**
 * The search a Page Build is built to win. Given by the job when it has one, else picked at spec from search volume
 * (Search Console queries for an existing page, Ahrefs volume for a new one) with the reason. The section agents
 * write toward it and the SEO review checks it.
 */
export type PageKeyword = {
  primary: string;
  secondary?: string[];
  /** Monthly searches, when the source has a number. */
  volume?: number | null;
  source: 'given' | 'search_console' | 'ahrefs';
  why: string;
};

/** The human's change to the target keyword, saved before approval; it wins over the spec's. */
export type KeywordOverride = { primary: string; secondary: string[]; updated_at: string };

/** The human's edits to a Page Build's sections, saved before approval; the build ships these instead. */
export type SectionsOverride = {
  sections: PageSection[];
  updated_at: string;
};

/**
 * A revision round's answer, on the page instead of deep in the markdown. `note` is the human's revise note, copied
 * verbatim by the CLI at submit (ai_jobs.revision_note is cleared then); `done` is what this round changed.
 */
export type SummaryRevision = {
  note: string;
  done: string[];
};

/**
 * Security Audit jobs (task_type 'security_audit'): one proven finding. Only what was shown to work counts: `proof`
 * is the exact request (as a stranger where possible) and what came back, trimmed. Every finding has a normal spec
 * Decision (`decision_id`): Fix / Skip / Later, or for a cascade Change it / Good cascade, keep / Later, so answering,
 * editing answers and approving work like any other job. Minor stuff is not a finding: one line in `parked`.
 */
export type SecuritySeverity = 'public' | 'login' | 'data_loss';
export type SecuritySection = 'security' | 'integrity';
export type SecurityBucket = 'codebase' | 'database' | 'live_config' | 'cascade' | 'drift' | 'backups';

/** A cascading foreign key, labelled, with what one real delete would take (counted in BEGIN … ROLLBACK). */
export type SecurityCascade = {
  constraint_name: string;
  from_table: string;
  to_table: string;
  label: 'good' | 'bad';
  why: string;
  delete_count: { sample: string; tables: { table: string; rows: number }[] };
};

export type SecurityFinding = {
  id: string;
  section: SecuritySection;
  bucket: SecurityBucket;
  title: string;
  severity: SecuritySeverity;
  /** Plain words: what a stranger can do. */
  exposed: string;
  proof: { request: string; result: string };
  /** What the fix changes. */
  fix: string;
  /** What might stop working. */
  could_break: string;
  /** The spec Decision that answers this finding. */
  decision_id: string;
  /** Cascade findings only. */
  cascade?: SecurityCascade;
};

/** The answers a security finding's decision offers. */
export const SECURITY_FINDING_OPTIONS = ['Fix', 'Skip', 'Later'] as const;
export const SECURITY_CASCADE_OPTIONS = ['Change it', 'Good cascade, keep', 'Later'] as const;

/** Security Audit builds: proof the fixes broke nothing. */
export type SecurityVerification = {
  /** Public pages and every app query as each role, recorded before the fix and re-run after. Any diff is rolled back. */
  snapshot: { pages: number; queries: number; diffs: { what: string; before: string; after: string }[]; passed: boolean };
  /** A throwaway user per role through the saved Playwright walkthrough. */
  walkthroughs: { role: string; steps: number; passed: boolean; note?: string }[];
  /** Flows skipped because they send email. */
  not_tested_email: string[];
  test_users_created: number;
  /** Must be 0: every throwaway user is deleted in the same run. */
  test_users_left: number;
  rolled_back: string[];
};

export type SecurityApplied = 'live' | 'pr' | 'rolled_back' | 'skipped';

export type SpecSummary = {
  title: string;
  /** One or two warm sentences to whoever created the job, by name. The page opens with it. */
  greeting?: string;
  sources: JobSource[];
  requests: JobRequest[];
  actions: ProposedAction[];
  repo: { name: string; note?: string };
  db: { prod?: string | null; preview?: string | null; note?: string };
  /** Caveats for the requester. The standout section. */
  need_to_know: string[];
  decisions: Decision[];
  /** Short, layman. */
  approach: string[];
  /** Deliberately not done; each should become its own card or a note. */
  parked: string[];
  /** Content jobs: the article's headings as spec'd. */
  outline?: OutlineHeading[];
  /** Content jobs: written by Cockpit only, never by an agent submit. */
  outline_override?: OutlineOverride | null;
  /** Page Build jobs: the page, section by section. */
  sections?: PageSection[];
  /** Page Build jobs: the human's edited sections; wins over `sections` when present. */
  sections_override?: SectionsOverride | null;
  /** Page Build jobs: the target search and why. */
  keyword?: PageKeyword;
  /** Page Build jobs: who the page is for and the action it asks of them. */
  buyer?: PageBuyer;
  /** Page Build jobs: the human's changed keyword; wins over `keyword` when present. */
  keyword_override?: KeywordOverride | null;
  /** Security Audit jobs: every proven finding, each answered by its decision. */
  findings?: SecurityFinding[];
  /** Present when this spec answered a revise note. */
  revision?: SummaryRevision | null;
};

/** Per request: how it worked before, how it works now, how to check it. */
export type BuildCheck = {
  /** A request id; on Security Audit jobs, a finding id. */
  request: string;
  before: string;
  after: string;
  how_to_check: string;
  url?: string;
  /** Security Audit jobs: where the fix went (a DB fix goes live once it passes its checks; code goes to a PR). */
  applied?: SecurityApplied;
};

export type BuildSummary = {
  /** Absent on content jobs with no publish repo: the article lives in Cockpit. */
  pr_url?: string | null;
  preview_url?: string | null;
  db_written?: string | null;
  /** Where the build differed from the spec. */
  deviations: string[];
  checks: BuildCheck[];
  /** Page Build jobs: the SEO review of the built page, as scripts/page-seo-check.mjs --json reported it. */
  seo_review?: SeoReview | null;
  /** Page Build jobs: a fresh agent's cold read of the preview as the buyer. */
  buyer_test?: BuyerTest | null;
  /** Security Audit jobs: snapshot diff, role walkthroughs, throwaway users, rollbacks. */
  verification?: SecurityVerification | null;
  /** Present when this build answered a revise note. */
  revision?: SummaryRevision | null;
};

/**
 * A fresh agent with only the preview URL and the buyer brief (no KB, no spec) reads the page as that buyer. After
 * five seconds above the fold it says what the page is, who it is for and what to do next; after a full read, what
 * would make them leave. passed = all three five-second answers match the brief.
 */
export type BuyerTest = {
  passed: boolean;
  what_it_is: string;
  for_whom: string;
  next_step: string;
  would_leave: string[];
  /** Rounds run before it passed (each failed round sent its sections back). */
  rounds?: number;
};

export type SeoCheck = { group: string; id?: string; status: 'pass' | 'warn' | 'fail'; label: string; detail?: string };
export type SeoReview = {
  url: string;
  liveUrl?: string;
  primary?: string | null;
  secondary?: string[];
  checks: SeoCheck[];
  summary: { pass: number; warn: number; fail: number };
  /** Warns the agent accepted, each with why. */
  accepted?: string[];
};

export type ConfirmedStatus = 'live' | 'partial' | 'not-live';

export type ApprovalSummary = {
  /** Absent on content jobs: approval verifies the piece, the deploy is a separate step. */
  live_url?: string | null;
  /** Paste-ready note to the client. */
  client_note: string;
  confirmed: { request: string; status: ConfirmedStatus; note?: string }[];
  follow_ups: string[];
};

export type OperatorSummary = {
  spec?: SpecSummary;
  build?: BuildSummary;
  approval?: ApprovalSummary;
};

export type OperatorStage = 'spec' | 'build' | 'approve' | 'live';

/** Which stage of the job page a status belongs to. */
export function operatorStageForStatus(status: AiJob['status']): OperatorStage {
  if (status === 'approved' || status === 'approval_in_progress') return 'approve';
  if (status === 'complete' || status === 'submitted') return 'live';
  return aiJobKind(status) === 'spec' ? 'spec' : 'build';
}

/** Trim a summary to what a job in this stage can honestly show. */
export function sliceSummaryForStage(summary: OperatorSummary, stage: OperatorStage): OperatorSummary {
  if (stage === 'spec') return { spec: summary.spec };
  if (stage === 'build') return { spec: summary.spec, build: summary.build };
  return summary;
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * The operator summary for a job, trimmed to its current stage, or null when
 * there is none (the panel then falls back to the raw markdown, exactly like
 * the drawer did). Trimming matters after a send-back: a job pushed from
 * complete back to build must not show a "live" column from its earlier run.
 */
export function operatorSummaryForJob(job: AiJob): OperatorSummary | null {
  if (!isObject(job.operator_summary)) return null;
  return sliceSummaryForStage(job.operator_summary, operatorStageForStatus(job.status));
}

// ---------------------------------------------------------------------------
// History: every stage write, so the page can stack versions and mark change.
// ---------------------------------------------------------------------------

export type SummaryStageKey = 'spec' | 'build' | 'approval';

export type SummaryVersion = {
  id: string;
  job_id: string;
  stage: SummaryStageKey;
  job_version: number;
  summary: SpecSummary | BuildSummary | ApprovalSummary;
  written_by: string | null;
  created_at: string;
};

export async function fetchSummaryVersions(jobId: string): Promise<SummaryVersion[]> {
  const { data, error } = await supabase
    .from('ai_job_summary_versions')
    .select('*')
    .eq('job_id', jobId)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return (data ?? []) as SummaryVersion[];
}

export function subscribeSummaryVersions(jobId: string, onChange: () => void): () => void {
  const channel = supabase
    .channel(`ai_job_summary_versions:${jobId}`)
    .on('postgres_changes', { event: '*', schema: 'public', table: 'ai_job_summary_versions', filter: `job_id=eq.${jobId}` }, onChange)
    .subscribe();
  return () => {
    supabase.removeChannel(channel);
  };
}

/** The stage key whose latest write belongs to the job's current page stage. */
export function stageKeyForOperatorStage(stage: OperatorStage): SummaryStageKey {
  if (stage === 'spec') return 'spec';
  if (stage === 'build') return 'build';
  return 'approval';
}

function stableJson(value: unknown): string {
  return JSON.stringify(value, (_k, v) => {
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      return Object.keys(v as Record<string, unknown>).sort().reduce<Record<string, unknown>>((acc, k) => {
        acc[k] = (v as Record<string, unknown>)[k];
        return acc;
      }, {});
    }
    return v;
  });
}

/** Per-request fingerprint of one stage's summary, so two versions can be compared row by row. */
function requestFingerprints(stage: SummaryStageKey, summary: SummaryVersion['summary']): Map<string, string> {
  const out = new Map<string, string>();
  if (stage === 'spec') {
    const s = summary as SpecSummary;
    const actions = new Map(s.actions.map((a) => [a.request, a.text]));
    for (const r of s.requests) out.set(r.id, stableJson({ text: r.text, kind: r.kind, risks: r.risks, action: actions.get(r.id) ?? null }));
  } else if (stage === 'build') {
    for (const c of (summary as BuildSummary).checks) out.set(c.request, stableJson(c));
  } else {
    for (const c of (summary as ApprovalSummary).confirmed) out.set(c.request, stableJson(c));
  }
  return out;
}

export type SummaryChanges = {
  /** Request ids whose row differs from the previous version of the same stage (or are new). */
  requests: Set<string>;
  /** The version being compared against, when there is one. */
  previous: SummaryVersion | null;
  /** True when this stage has only ever been written once. */
  first: boolean;
};

/** What changed in the current stage since its previous write. */
export function summaryChangesSince(job: AiJob, versions: SummaryVersion[]): SummaryChanges {
  const stageKey = stageKeyForOperatorStage(operatorStageForStatus(job.status));
  const current = job.operator_summary?.[stageKey];
  const mine = versions.filter((v) => v.stage === stageKey);
  if (!current || mine.length < 2) return { requests: new Set(), previous: null, first: mine.length <= 1 };
  // The latest row mirrors the column; compare against the one before it.
  const previous = mine[mine.length - 2];
  const before = requestFingerprints(stageKey, previous.summary);
  const after = requestFingerprints(stageKey, current);
  const changed = new Set<string>();
  for (const [id, fp] of after) if (before.get(id) !== fp) changed.add(id);
  return { requests: changed, previous, first: false };
}

// ---------------------------------------------------------------------------
// Tiny tags for list rows: what needs the human before opening the job.
// ---------------------------------------------------------------------------

export type SummaryTag = { label: string; tone: 'accent' | 'warn' | 'neutral' };

const RISK_TAG: Record<JobRisk, string> = {
  email: 'sends email',
  payments: 'payments',
  auth: 'logins',
  'prod-data': 'live data',
  sms: 'sends sms',
};

export function summaryChipTags(job: AiJob): SummaryTag[] {
  const spec = job.operator_summary?.spec;
  if (!spec) return [];
  const tags: SummaryTag[] = [];
  const open = spec.decisions.filter((d) => !d.answer).length;
  if (open > 0) tags.push({ label: open === 1 ? '1 decision' : `${open} decisions`, tone: 'accent' });
  if (job.task_type === 'security_audit' && spec.findings) {
    const pub = spec.findings.filter((f) => f.severity === 'public').length;
    if (pub) tags.push({ label: `${pub} public`, tone: 'warn' });
    tags.push({ label: `${spec.findings.length} ${spec.findings.length === 1 ? 'finding' : 'findings'}`, tone: 'neutral' });
    return tags;
  }
  const risks = new Set<JobRisk>();
  for (const r of spec.requests) for (const risk of r.risks ?? []) risks.add(risk);
  for (const risk of risks) tags.push({ label: RISK_TAG[risk], tone: 'warn' });
  tags.push({ label: `${spec.requests.length} ${spec.requests.length === 1 ? 'request' : 'requests'}`, tone: 'neutral' });
  return tags;
}

/** Client name for the header eyebrow and queue rows. AiJob rows carry client_id only; the
    joined `client.name` / `task.client.name` appear when the caller fetched them (fetchQueueJobs does). */
export function operatorClientName(job: AiJob): string | null {
  const joined = job.client?.name || job.task?.client?.name;
  if (joined) return joined;
  const brief = job.brief as { client_name?: string };
  if (brief && typeof brief.client_name === 'string' && brief.client_name) return brief.client_name;
  if (job.id === PETES_LOOM_JOB_ID) return PETES_LOOM_CLIENT_NAME;
  return null;
}
